<template>
  <Flow
    v-if="showPopover"
    transition-group-class-name="page-modal page-modal--page modal modal--page container-fluid"
    role="dialog"
    :has-flow-container="false"
    @left="onLeft"
  >
    <template v-if="filteredPages">
      <LFSection v-for="page in filteredPages" :key="page.state.modelId" :model="page" />
    </template>
    <slot />
  </Flow>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, watch, ref, nextTick, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { getScrollbarWidth, inIframe } from '@/utilities/Utilities';
import { useStore } from '@/store';
import { PopoverModel } from '@/models/PopoverModel';
import Flow from '@/components/Flow/Flow.vue';
import useDevice from '@/hooks/useDevice';
import { defaultLeaveAnimation } from '@/components/Flow/Types';
import { recursivelyWaitForPromises } from '@/utilities/virtualDom';
import { onResize } from '@/services/iframe-embed';
import { sendEvent } from '@/utilities/iFrameUtils';

export default defineComponent({
  name: 'Popover',
  components: { Flow },
  props: {
    pageId: {
      type: Number
    },
    pages: {
      type: Array as PropType<PopoverModel[]>
    },
    backgroundColor: {
      type: String
    },
    textColor: {
      type: String
    }
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const showPopover = ref(false);
    const transitionDelay = ref(defaultLeaveAnimation.duration);

    const scrollbarWidth = getScrollbarWidth();

    const filteredPages = computed(() => {
      if (props && props.pages) {
        return props.pages.filter((page) => page.state.id === props.pageId);
      }
      return [];
    });

    const onLeft = () => {
      if (!props.pageId) {
        setTimeout(() => {
          const campaignElement = document.querySelector<HTMLDivElement>('.campaign');

          if (campaignElement) {
            campaignElement.style.paddingRight = `0px`;
          }

          showPopover.value = false;
        }, transitionDelay.value);
      }
    };

    watch(
      showPopover,
      () => {
        if (showPopover.value) {
          document.body.style.overflow = 'hidden';
        } else {
          store.dispatch('utility/removeBodyClass', 'site--popover-active');
          document.body.style.overflow = '';

          if (inIframe()) {
            document.body.style.height = '';
            onResize();
          }
        }
      },
      {
        immediate: true
      }
    );

    watch(
      filteredPages,
      () => {
        if (filteredPages.value[0]) {
          transitionDelay.value =
            filteredPages.value[0]?.state.config.settings.state.advanced?.transitions?.leaveDuration ??
            defaultLeaveAnimation.duration;
        }

        if (filteredPages.value.length === 0) {
          onLeft();
        } else {
          showPopover.value = !!props.pageId;
        }
      },
      {
        immediate: true
      }
    );

    const currentInstance = getCurrentInstance();

    watch(
      () => props.pageId,
      async (newValue, oldValue) => {
        if (newValue && oldValue && newValue !== oldValue && newValue > 0 && oldValue > 0) {
          showPopover.value = false;
        }
        if (newValue && filteredPages.value.length > 0) {
          await store.dispatch('utility/addBodyClass', 'site--popover-active');
          await nextTick();
          showPopover.value = true;

          const { isMobile, isTablet } = useDevice();
          const siteEditorViewer = document.querySelector('.site-editor__viewer');

          if ((isMobile || isTablet) && siteEditorViewer) {
            siteEditorViewer?.scrollTo(0, 0);
          } else {
            window.scrollTo(0, 0);
          }

          sendEvent('scroll-to-top');

          if (route.query && !route.query.edit) {
            const campaignElement = document.querySelector<HTMLDivElement>('.campaign');

            if (campaignElement) {
              campaignElement.style.paddingRight = `${scrollbarWidth}px`;
            }
          }

          document.body.style.overflow = 'hidden';

          /**
           * Iframe scrolling solution for when viewing popovers on a mobile device.
           * This sets the height on the body element to match taht of the popover.
           *
           * Making it possible for the user to scroll on the top-layer instead
           * of on the popover element. This makes for a much better scrolling experience.
           */
          if (inIframe()) {
            if (currentInstance?.vnode) {
              await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter', 3);
            }

            await nextTick();

            setTimeout(() => {
              const pageModal = document.querySelector<HTMLDivElement>('.page-modal');

              if (pageModal) {
                document.body.style.height = `${pageModal.offsetHeight}px`;
                onResize();
              }
            });
          }
        }
      }
    );

    return {
      showPopover,
      filteredPages,
      onLeft
    };
  }
});
</script>

<style lang="scss">
.page-modal {
  position: fixed;
  z-index: 1060;
  width: 100%;
  height: 100%;
  display: block;
  background-color: transparent !important;
  overflow-x: hidden !important;
  overflow-y: scroll !important;
  -webkit-overflow-scrolling: touch !important;
  transform: translate(0, 0);

  > .section {
    min-height: 100%;
    position: relative !important;
    overflow: hidden;
  }
}

.site--editmode-on {
  .page-modal {
    .section {
      width: auto !important;
    }
  }
}

/* ----------- Scrolling fixes for iframe popover. To avoid double scrolling ----------- */

html .site--iframe.site--popover-active.site--mobile {
  overflow: auto !important;

  .page-modal {
    min-height: 100vh;
    height: auto !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    transform: none !important;
    position: absolute !important;
  }
}
</style>
